import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MailWarning, Check } from 'lucide-react';
import { useClientAuth } from '../contexts/ClientAuthContext';

// Shown across the portal until the logged-in user confirms their email. Users
// are logged in immediately on signup (hybrid double opt-in), so this nudges the
// remaining confirmation without blocking access.
export default function EmailVerificationBanner() {
  const { user, resendVerification } = useClientAuth();
  const { t } = useTranslation();
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  // Only render once we know the user and they are NOT yet verified. `undefined`
  // (older API response) is treated as verified to avoid a false nag.
  if (!user || user.emailVerified !== false) return null;

  const resend = async () => {
    setState('sending');
    try {
      await resendVerification();
      setState('sent');
    } catch {
      setState('error');
    }
  };

  return (
    <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <div className="flex items-center gap-2 flex-1">
        <MailWarning size={18} className="shrink-0 text-amber-600" />
        <span>
          {t('portal.verifyEmail.banner', 'Confirma tu email ({{email}}) para asegurar tu cuenta.', { email: user.email })}
        </span>
      </div>
      {state === 'sent' ? (
        <span className="inline-flex items-center gap-1 font-medium text-emerald-700">
          <Check size={16} /> {t('portal.verifyEmail.sent', 'Email enviado')}
        </span>
      ) : (
        <button
          onClick={resend}
          disabled={state === 'sending'}
          className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 font-medium text-white hover:bg-amber-700 disabled:opacity-60"
        >
          {state === 'sending'
            ? t('portal.verifyEmail.sending', 'Enviando…')
            : state === 'error'
              ? t('portal.verifyEmail.retry', 'Reintentar')
              : t('portal.verifyEmail.resend', 'Reenviar confirmación')}
        </button>
      )}
    </div>
  );
}
